import { ModalOpenButton } from "@msys/ui";
import FindReplaceIcon from "@mui/icons-material/FindReplace";
import { IconButton } from "@mui/material";
import React from "react";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { buildFisSolrUrl, type useFisSolr } from "../../sap-s4-hana/hooks.js";
import { FisSolrSearchModal } from "../../sap-s4-hana/richter-frenzel/FisSolrSearchModal.js";
import type { ProductCreateModalProps } from "../../products/modals/ProductCreateModal.js";
import { ProductCreateModal } from "../../products/modals/ProductCreateModal.js";
import { ProductSearchModal } from "../../products/modals/ProductSearchModal.js";
import type { ProductFiltersFormValues } from "../../products/filters/ProductFilterFields.js";
import type { ProductSearchItem__ProductSearchResultFragment } from "../../products/Product.generated.js";

export type ProductReplaceCreateButtonOnSelectFn = (
  product: Pick<
    ProductSearchItem__ProductSearchResultFragment,
    "articleNumber" | "supplierId" | "status"
  >,
  filters: ProductFiltersFormValues
) => Promise<void> | void;

interface Props {
  docId: string;
  itemId: string;
  hasProduct: boolean;
  canEdit: boolean;
  showFisSolr: boolean;
  sapIntegrationSettings: ReturnType<
    typeof useFisSolr
  >["sapIntegrationSettings"];
  productSearchFilterDefinitions:
    | {
        productTypeIdFilter?: { valueId: string } | null;
      }
    | null
    | undefined;
  onProductSelect: ProductReplaceCreateButtonOnSelectFn;
  onProductCreate: ProductCreateModalProps["handleSubmit"];
}

export const ProductReplaceCreateButton = ({
  docId,
  itemId,
  hasProduct,
  canEdit,
  showFisSolr,
  sapIntegrationSettings,
  productSearchFilterDefinitions,
  onProductSelect,
  onProductCreate,
}: Props) => {
  const [isFisSolrModalOpen, setIsFisSolrModalOpen] = React.useState(false);

  if (!canEdit) return null;

  // FIS SOLR replace (mutually exclusive with standard replace)
  if (hasProduct && showFisSolr) {
    return (
      <>
        <IconButton
          size="small"
          onClick={() => {
            const url = buildFisSolrUrl(sapIntegrationSettings, {
              itemId,
              quoteId: docId,
              mode: "REPLACE",
            });
            if (url) {
              window.open(url, "fis-solr-tab");
              setIsFisSolrModalOpen(true);
            }
          }}
        >
          <FindReplaceIcon fontSize="small" color="secondary" />
        </IconButton>
        {isFisSolrModalOpen && (
          <FisSolrSearchModal
            handleClose={() => setIsFisSolrModalOpen(false)}
          />
        )}
      </>
    );
  }

  const canChangeProductType =
    !productSearchFilterDefinitions?.productTypeIdFilter;

  const icon = <FindReplaceIcon fontSize="small" color="secondary" />;

  return (
    <RestrictedByCapabilityWithDebug
      capability="pim"
      otherwise={
        <ModalOpenButton
          Modal={ProductCreateModal}
          modalProps={{ handleSubmit: onProductCreate }}
        >
          <IconButton size="small">{icon}</IconButton>
        </ModalOpenButton>
      }
    >
      <ModalOpenButton
        Modal={ProductSearchModal}
        modalProps={{
          handleProductChoice: onProductSelect,
          handleProductCreate: onProductCreate,
          canChangeProductType,
        }}
      >
        <IconButton size="small">{icon}</IconButton>
      </ModalOpenButton>
    </RestrictedByCapabilityWithDebug>
  );
};
